import { RefObject, useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { AIWidget } from '@/ui/components/ai-widget/ai-widget'
import { useChatContext } from './chat-context'
import type { ChatFollowupKind } from './types'
import { ANALYTICS_TAB } from './analytics-mcp-token'
import {
  SUGGESTED_PROMPTS,
  promptMarker,
  promptNameFromMarker,
  type SuggestedPromptName
} from './suggested-prompts'
import { useChat } from './use-chat'
import { useAssistantTracking } from './use-assistant-tracking'
import { ChatFeedback } from './chat-feedback'
import { Aurora } from './aurora'
import { renderMarkdown } from './markdown'
import { DOCK_MIN_WIDTH, DOCK_STEP, useDockWidth } from './use-dock-width'

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'

const PANEL = 'flex h-full flex-col overflow-hidden text-white'

// Human-readable screen name when a tab has no translation key yet.
function humanizeTab(tab: string): string {
  const spaced = tab.replace(/-/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// A suggestion travels as a marker, so both the thread and the feedback it may
// carry show what was clicked, not the marker.
function useAskedQuestion(content: string) {
  const { t } = useTranslation()
  const promptName = promptNameFromMarker(content)
  return {
    promptName,
    text: promptName
      ? t(`configuration_chat.suggested.prompts.${promptName}`)
      : content
  }
}

function Bubble({ role, content }: { role?: string; content?: string }) {
  const isUser = role === 'user'
  const { text: userText } = useAskedQuestion(content ?? '')
  return (
    <div
      className={
        isUser
          ? 'ml-auto max-w-[85%] break-words rounded-2xl rounded-tr-sm bg-gradient-to-br from-[#1790ff] to-[#1b75eb] px-4 py-3 text-sm leading-relaxed text-white'
          : 'flex max-w-[85%] flex-col gap-2 break-words rounded-2xl rounded-tl-sm border border-white/10 bg-white/5 px-4 py-3 text-sm leading-relaxed text-white/90'
      }
    >
      {isUser ? userText : renderMarkdown(content ?? '')}
    </div>
  )
}

// Feedback on one assistant reply, carrying the question that produced it.
function ReplyFeedback({
  question,
  answer,
  turnIndex
}: {
  question: string
  answer: string
  turnIndex: number
}) {
  const asked = useAskedQuestion(question)
  return (
    <ChatFeedback
      turnIndex={turnIndex}
      question={asked.text}
      answer={answer}
      promptName={asked.promptName}
    />
  )
}

function RulesUpdatedCard() {
  const { t } = useTranslation()
  return (
    <div className="max-w-[85%] rounded-2xl border border-[#10fab8]/40 bg-gradient-to-br from-[#10fab8]/10 to-[#a2eeff]/10 px-4 py-3 text-sm">
      <div className="flex items-center gap-2 font-semibold text-[#a2eeff]">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
        {t('configuration_chat.draft_updated')}
      </div>
      <p className="mt-1 text-white/70">
        {t('configuration_chat.draft_updated_hint')}
      </p>
    </div>
  )
}

// The file formats the export chip opens onto; each sends its own request message.
const EXPORT_FORMATS = [
  {
    label: 'Excel (.xlsx)',
    message: 'configuration_chat.followups.export_xlsx'
  },
  { label: 'CSV', message: 'configuration_chat.followups.export_csv' }
] as const

const FOLLOWUP_CHIP_CLASS =
  'flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-to-br from-[#10fab8] to-[#a2eeff] px-3 py-1.5 text-xs font-semibold text-[#020c1c] outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#a2eeff]'

// Actions the assistant offered under its last reply. Export is the primary one:
// the single light element on a dark panel. It opens onto a format choice.
function FollowupRow({
  followups,
  onPick
}: {
  followups: ChatFollowupKind[]
  onPick: (messageKey: string) => void
}) {
  const { t } = useTranslation()
  const [choosingFormat, setChoosingFormat] = useState(false)
  if (choosingFormat) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {EXPORT_FORMATS.map(({ label, message }) => (
          <button
            key={label}
            type="button"
            onClick={() => onPick(message)}
            className={FOLLOWUP_CHIP_CLASS}
          >
            {label}
          </button>
        ))}
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      {followups.map((kind) => (
        <button
          key={kind}
          type="button"
          onClick={() => setChoosingFormat(true)}
          className={FOLLOWUP_CHIP_CLASS}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 4v10M8 11l4 4 4-4M5 19h14" />
          </svg>
          {t(`configuration_chat.followups.${kind}`)}
        </button>
      ))}
    </div>
  )
}

// The predefined MCP prompts: an open list on an empty thread, behind a chip once the
// conversation started. One component, so the two states cannot drift apart.
function SuggestedPromptList({
  onPick
}: {
  onPick: (name: SuggestedPromptName) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.09em] text-white/45">
        {t('configuration_chat.suggested.heading')}
      </span>
      <div className="flex flex-col gap-2">
        {SUGGESTED_PROMPTS.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => onPick(name)}
            className="flex items-center gap-2.5 rounded-xl border border-white/[0.12] bg-white/[0.04] px-3 py-2.5 text-left outline-none transition-colors hover:border-[#a2eeff]/60 hover:bg-white/[0.07] focus-visible:border-[#a2eeff] focus-visible:ring-2 focus-visible:ring-[#a2eeff]"
          >
            <span className="flex-1 text-[13px] leading-snug text-white">
              {t(`configuration_chat.suggested.prompts.${name}`)}
            </span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0 text-white/35"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  )
}

function SuggestionsToggle({
  isOpen,
  onToggle
}: {
  isOpen: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="inline-flex items-center gap-1.5 self-start whitespace-nowrap rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-[#a2eeff]"
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0"
      >
        <path d="M12 4l1.7 6.3L20 12l-6.3 1.7L12 20l-1.7-6.3L4 12l6.3-1.7z" />
      </svg>
      {t('configuration_chat.suggested.show', {
        total: SUGGESTED_PROMPTS.length
      })}
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`shrink-0 text-white/55 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>
  )
}

function TypingIndicator() {
  const { t } = useTranslation()
  return (
    <div
      aria-label={t('configuration_chat.typing')}
      className="flex max-w-[85%] items-center gap-1 rounded-2xl rounded-tl-sm border border-white/10 bg-white/5 px-4 py-3"
    >
      <span className="size-1.5 animate-bounce rounded-full bg-white/50" />
      <span
        className="size-1.5 animate-bounce rounded-full bg-white/50"
        style={{ animationDelay: '150ms' }}
      />
      <span
        className="size-1.5 animate-bounce rounded-full bg-white/50"
        style={{ animationDelay: '300ms' }}
      />
    </div>
  )
}

function ErrorBubble({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div
      role="alert"
      className="max-w-[85%] rounded-2xl rounded-tl-sm border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-white/90"
    >
      <p>{t('configuration_chat.error')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 rounded-lg border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-white outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-[#a2eeff]"
      >
        {t('configuration_chat.retry')}
      </button>
    </div>
  )
}

// Drag (or arrow-key) target on the panel's inner edge. Dragging left widens the
// panel, which narrows the page beside it.
function ResizeHandle({
  width,
  maxWidth,
  onResizeStart,
  onNudge
}: {
  width: number
  maxWidth: number
  onResizeStart: (event: React.PointerEvent) => void
  onNudge: (delta: number) => void
}) {
  const { t } = useTranslation()
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    onNudge(e.key === 'ArrowLeft' ? DOCK_STEP : -DOCK_STEP)
  }
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('configuration_chat.resize')}
      aria-valuenow={width}
      aria-valuemin={DOCK_MIN_WIDTH}
      aria-valuemax={maxWidth}
      tabIndex={0}
      onPointerDown={onResizeStart}
      onKeyDown={onKeyDown}
      className="group absolute inset-y-0 left-0 z-10 flex w-3 cursor-col-resize touch-none items-center justify-center outline-none"
    >
      <span className="h-8 w-1 rounded-full bg-white/15 transition-colors group-hover:bg-white/40 group-focus-visible:bg-[#a2eeff]" />
    </div>
  )
}

function useFocusOnMount(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    const panel = ref.current
    if (!panel) return
    const firstInput = panel.querySelector<HTMLElement>('input:not([disabled])')
    ;(firstInput ?? panel.querySelector<HTMLElement>(FOCUSABLE))?.focus()
  }, [ref])
}

export function ChatPanel({
  onClose,
  docked = false
}: {
  onClose: () => void
  docked?: boolean
}) {
  return docked ? (
    <DockedPanel onClose={onClose} />
  ) : (
    <OverlayPanel onClose={onClose} />
  )
}

// Wide viewports dock the panel as a column of the app shell: the page shrinks
// beside it instead of disappearing under it, so nothing is hidden while the
// assistant edits the screen the user is reading.
function DockedPanel({ onClose }: { onClose: () => void }) {
  const reduce = useReducedMotion()
  const panelRef = useRef<HTMLDivElement>(null)
  const { width, maxWidth, isResizing, startResize, nudgeWidth } =
    useDockWidth()
  useFocusOnMount(panelRef)

  // Docked is not modal, so Escape only closes when focus is inside the panel —
  // otherwise it would swallow Escape from the page the user is working on.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }

  return (
    <motion.div
      initial={{ width: 0 }}
      animate={{ width }}
      exit={{ width: 0 }}
      transition={
        reduce || isResizing
          ? { duration: 0 }
          : { duration: 0.28, ease: [0.22, 1, 0.36, 1] }
      }
      className="h-screen shrink-0 overflow-hidden bg-shell"
    >
      <div
        ref={panelRef}
        role="complementary"
        aria-label="REVER AI"
        onKeyDown={onKeyDown}
        style={{ width }}
        className="h-full py-2 pr-2"
      >
        {/* A shade lighter than the #000D1F shell it sits on, so the card edge
            reads next to the page card; the overlay needs no such lift. */}
        <div
          className={`${PANEL} relative rounded-[20px] border border-white/10 bg-[#08182f]`}
        >
          {/* After the body so the mount-focus fallback and Tab order reach the
              panel's own controls before the splitter. */}
          <ChatBody onClose={onClose} />
          <ResizeHandle
            width={width}
            maxWidth={maxWidth}
            onResizeStart={startResize}
            onNudge={nudgeWidth}
          />
        </div>
      </div>
    </motion.div>
  )
}

// Narrow viewports have no room to squeeze the page, so the panel slides over it
// as a modal sheet: focus is trapped and Escape closes it from anywhere.
function OverlayPanel({ onClose }: { onClose: () => void }) {
  const reduce = useReducedMotion()
  const panelRef = useRef<HTMLElement>(null)
  useFocusOnMount(panelRef)

  const trapFocus = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !panelRef.current) return
    const items = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  return (
    <motion.aside
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="REVER AI"
      onKeyDown={trapFocus}
      initial={reduce ? { opacity: 0 } : { x: '100%' }}
      animate={reduce ? { opacity: 1 } : { x: 0 }}
      exit={reduce ? { opacity: 0 } : { x: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 320 }}
      className={`${PANEL} fixed inset-y-0 right-0 z-50 w-[min(400px,92vw)] border-l border-white/10 bg-[#020c1c] shadow-2xl`}
    >
      <ChatBody onClose={onClose} />
    </motion.aside>
  )
}

function ChatBody({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation()
  const { activeContext } = useChatContext()
  const {
    messages,
    send,
    retry,
    reset,
    isPending,
    error,
    rulesUpdatedIndexes,
    followups
  } = useChat()
  const { trackExportClicked } = useAssistantTracking()
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [input, setInput] = useState('')
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)

  const tabName = activeContext?.tab
  const tabKey = tabName ? `configuration_chat.tabs.${tabName}` : ''
  // Fall back to a humanized name if the screen has no translation key yet, so
  // we never leak the raw key (i18n.exists avoids a missing-key report too).
  const label = tabName
    ? i18n.exists(tabKey)
      ? t(tabKey)
      : humanizeTab(tabName)
    : undefined
  const canEdit = activeContext?.applyResult !== undefined
  const canSend = input.trim().length > 0 && !isPending && !!activeContext
  // Only the analytics tab has predefined prompts.
  const showSuggestions = activeContext?.tab === ANALYTICS_TAB && !isPending

  const greeting = !activeContext
    ? t('configuration_chat.greeting_generic')
    : canEdit
      ? t('configuration_chat.greeting_editable', { screen: label })
      : t('configuration_chat.greeting_readonly', { screen: label })

  // Subtitle and placeholder follow the same 3-way split as the greeting: a
  // read-only screen (the analytics tabs) must not be told it can change a rule.
  const subtitle = !activeContext
    ? t('configuration_chat.subtitle_generic')
    : canEdit
      ? t('configuration_chat.subtitle')
      : t('configuration_chat.subtitle_readonly')

  const placeholder = !activeContext
    ? t('configuration_chat.placeholder_disabled')
    : canEdit
      ? t('configuration_chat.placeholder')
      : t('configuration_chat.placeholder_readonly')

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, isPending, error])

  const submit = () => {
    if (!canSend) return
    send(input)
    setInput('')
  }

  const startNewConversation = () => {
    reset()
    setInput('')
    setSuggestionsOpen(false)
    inputRef.current?.focus()
  }

  // The chip's own label is the request: short, already translated, and it reads
  // naturally as the user's message in the thread.
  const pickFollowup = (messageKey: string) => send(t(messageKey), 'followup')

  // The marker, not the label: the question itself lives in the MCP. The language
  // rides along so the English prompt text doesn't decide the reply language.
  const pickSuggestion = (name: SuggestedPromptName) => {
    setSuggestionsOpen(false)
    send(promptMarker(name, i18n.language), 'suggested_prompt')
  }

  // Delegated so `renderMarkdown` stays a pure renderer. Only assistant replies
  // are linkified, and the only link they carry is the export download.
  const onThreadClick = (e: React.MouseEvent) => {
    const link = (e.target as HTMLElement).closest('a')
    if (!link) return
    trackExportClicked({
      label: link.textContent ?? '',
      href: link.getAttribute('href') ?? ''
    })
  }

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <>
      <header className="relative overflow-hidden border-b border-white/10 p-4">
        <Aurora />
        <div className="relative flex items-center gap-3">
          <AIWidget />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold">
              REVER <span className="text-[#a2eeff]">AI</span>
            </span>
            <span className="text-[11px] text-white/70">{subtitle}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={startNewConversation}
              disabled={isPending || messages.length === 0}
              aria-label={t('configuration_chat.new_conversation')}
              title={t('configuration_chat.new_conversation')}
              className="flex size-8 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-white/70 outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-[#a2eeff] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('configuration_chat.close')}
              className="flex size-8 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-white/70 outline-none transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-[#a2eeff]"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </div>
        {label && (
          <span className="relative mt-3 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#a2eeff] to-[#10fab8] px-2.5 py-1 text-[11px] font-semibold text-[#020c1c]">
            <span className="size-1.5 rounded-full bg-[#020c1c]" />
            {t('configuration_chat.context', { screen: label })}
          </span>
        )}
      </header>

      <div
        ref={scrollRef}
        onClick={onThreadClick}
        aria-live="polite"
        className="flex flex-1 flex-col gap-3 overflow-y-auto p-4"
      >
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-white/10 bg-white/5 px-4 py-3 text-sm leading-relaxed text-white/90">
          {greeting}
        </div>
        {messages.map((message, index) => (
          <div key={index} className="flex flex-col gap-3">
            <Bubble role={message.role} content={message.content} />
            {rulesUpdatedIndexes.has(index) && <RulesUpdatedCard />}
            {message.role === 'assistant' && (
              <ReplyFeedback
                turnIndex={index}
                question={messages[index - 1]?.content ?? ''}
                answer={message.content ?? ''}
              />
            )}
          </div>
        ))}
        {followups.length > 0 && !isPending && (
          <FollowupRow followups={followups} onPick={pickFollowup} />
        )}
        {showSuggestions && messages.length > 0 && (
          <SuggestionsToggle
            isOpen={suggestionsOpen}
            onToggle={() => setSuggestionsOpen((open) => !open)}
          />
        )}
        {showSuggestions && (messages.length === 0 || suggestionsOpen) && (
          <SuggestedPromptList onPick={pickSuggestion} />
        )}
        {isPending && <TypingIndicator />}
        {error && <ErrorBubble onRetry={retry} />}
      </div>

      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-3 py-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onInputKeyDown}
            disabled={!activeContext}
            placeholder={placeholder}
            aria-label={t('configuration_chat.message_label')}
            className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/40 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label={t('configuration_chat.send')}
            className={`flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#1790ff] to-[#1b75eb] text-white outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-[#a2eeff] ${
              canSend ? '' : 'opacity-50'
            }`}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 12l16-8-6 16-3-6-7-2z" />
            </svg>
          </button>
        </div>
      </div>
    </>
  )
}
